import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Download, Eye, FileWarning, Send, Sparkles } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { Pill, fmtDate, fmtRelative, label } from '@/features/dossier/components/primitives'
import {
  addPost, approveDraft, dismissThread, downloadThreadMedia, editDraft, escalateThread,
  getThread, listAccounts, listPersonas, noteLurker, recordAssessment, recordDraft,
  refreshThread, rejectDraft, requestAssessment, requestDrafts,
  type Account, type Persona, type ThreadDetail, type ThreadMedia,
} from './api'
import { Metric, PlatformChip, SentimentChip, StatusChip, ThreatScore, Velocity } from './components'
import { DraftAsset } from './DraftAsset'
import type { CouncilRun } from './api'

const TACTICS = [
  ['COGNITIVE_DECOUPLING', 'Strip the emotion, state the reality — against fear-mongering'],
  ['THE_ANCHOR', 'Ignore nine points, end the weakest one — against a Gish Gallop'],
  ['FULL_CONTEXT_DROP', 'What came before and after — against an out-of-context quote'],
  ['FACT_CHECK', 'A checkable claim, checked'],
  ['PIVOT', 'True but small; the record answers better on a larger question'],
  ['HUMOUR', 'Rarely, and never about a person'],
  ['ASTROTURF_INOCULATION', 'Point at the repetition — needs a coordination signal'],
  ['LURKER_OUTREACH', 'To somebody who engaged without commenting, under their own comment'],
  ['COUNTER_MEME', 'A generated image answering a mocking one'],
] as const

const SENTIMENTS = ['HOSTILE', 'NEGATIVE', 'MIXED', 'NEUTRAL', 'SUPPORTIVE']
const FRAMINGS = ['EMOTIONAL', 'LOGICAL', 'MIXED', 'SATIRICAL']

/**
 * One thread, as the conversation it actually is.
 *
 * ## Why a chat layout and not a table
 *
 * Because the thing being judged is an ARGUMENT, and an argument read as rows loses the shape
 * that makes it persuasive or weak. An operator deciding whether to answer needs to see what a
 * bystander sees: who said what, in order, and where our own replies sit among them. Our posts
 * are aligned right and marked, so "what does this thread look like to somebody scrolling past"
 * is answerable at a glance — which is the actual question.
 *
 * ## Drafts appear inline, where the reply would land
 *
 * Not in a separate pane. A draft is a proposed message in a conversation, and reviewing it
 * anywhere other than in that conversation is how a reply that reads fine in isolation turns
 * out to be answering the wrong post.
 *
 * ## Nothing on this page posts without an explicit approval
 *
 * Approve names an account and, server-side, the authenticated caller. The button says
 * "Approve & deploy" rather than "Send" because the operator's name — not the model's — goes
 * on the ledger row, and the wording should not soften that.
 */
export function ThreadPage() {
  const { id = '' } = useParams()
  const authorities = useSession((s) => s.authorities)
  const canTriage = hasPermission(authorities, 'warroom:triage')
  const canDraft = hasPermission(authorities, 'warroom:draft')
  const canDeploy = hasPermission(authorities, 'warroom:deploy')

  const [data, setData] = useState<ThreadDetail | null>(null)
  const [personas, setPersonas] = useState<Persona[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const [reply, setReply] = useState('')
  const [replyAuthor, setReplyAuthor] = useState('')
  const [replyLurker, setReplyLurker] = useState(false)

  const [assessing, setAssessing] = useState(false)
  const [score, setScore] = useState(5)
  const [sentiment, setSentiment] = useState('NEGATIVE')
  const [framing, setFraming] = useState('EMOTIONAL')
  const [rationale, setRationale] = useState('')

  const [drafting, setDrafting] = useState(false)
  const [tactic, setTactic] = useState<string>('FACT_CHECK')
  const [personaId, setPersonaId] = useState('')
  const [draftBody, setDraftBody] = useState('')
  const [draftRationale, setDraftRationale] = useState('')

  const [accountId, setAccountId] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [rejecting, setRejecting] = useState<string | null>(null)
  const [rejectNote, setRejectNote] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await getThread(id))
    } catch (err) {
      setError(explain(err))
    }
  }, [id])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listPersonas().then(setPersonas).catch(() => setPersonas([]))
    void listAccounts().then((rows) => {
      setAccounts(rows)
      setAccountId((current) => current || rows.find((a) => a.active)?.id || '')
    }).catch(() => setAccounts([]))
  }, [])

  async function run(work: () => Promise<unknown>, message?: string) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await work()
      if (message) setNotice(message)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  if (error && !data) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Opening the thread" />

  const { thread, posts, assessments, drafts, deployments, lurkers, coordination,
          councilRuns, media } = data
  const latest = assessments[0]
  const liveSignals = coordination.filter((c) => c.stoodDownAt == null)
  const postable = accounts.filter((a) => a.active)

  return (
    <div className="space-y-3">
      {error && <ErrorStrip message={error} />}
      {notice && (
        <p className="rounded border border-emerald-500/30 bg-emerald-500/5 px-3 py-2
                      text-[11px] text-emerald-700 dark:text-emerald-400">{notice}</p>
      )}

      {/* ── the header: who, where, how fast ── */}
      <Panel>
        <div className="space-y-2 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-48 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <PlatformChip code={thread.platformCode} />
                {thread.authorHandle && (
                  <span className="text-[13px] font-medium">@{thread.authorHandle}</span>
                )}
                <StatusChip code={thread.statusCode} />
                <ThreatScore score={thread.threatScore} />
                <SentimentChip code={thread.sentimentCode} />
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                About{' '}
                {/* Straight through to every other thread about them, and the council question. */}
                {thread.subjectId ? (
                  <Link to={`/war-room/subjects/${thread.subjectId}`}
                        className="font-semibold text-primary hover:underline">
                    {thread.subjectName ?? 'an unnamed subject'}
                  </Link>
                ) : (
                  <b>an unnamed subject</b>
                )} · first seen{' '}
                {fmtRelative(thread.firstSeenAt)}
                {thread.editedSeen && ' · a post in this thread was edited after we captured it'}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3">
                <Velocity value={thread.velocity} />
                <Metric label="likes" value={thread.likes} />
                <Metric label="replies" value={thread.replies} />
                <Metric label="reposts" value={thread.reposts} />
                {thread.url && (
                  <a href={thread.url} target="_blank" rel="noreferrer"
                     className="text-[10px] text-primary underline">open on {thread.platformCode}</a>
                )}
              </div>
            </div>
            {canTriage && (
              <div className="flex flex-wrap gap-1.5">
                <button type="button" disabled={busy}
                        onClick={() => void run(() => refreshThread(id), 'Re-polled.')}
                        className="rounded border border-border px-2 py-1 text-[11px]">
                  Re-poll
                </button>
                <button type="button" disabled={busy}
                        onClick={() => void run(() => escalateThread(id), 'Escalated.')}
                        className="rounded border border-border px-2 py-1 text-[11px]">
                  Escalate
                </button>
                <button type="button" disabled={busy}
                        onClick={() => {
                          const reason = window.prompt(
                            'Why does this thread need nothing? (10 characters minimum — it '
                            + 'goes on the record)')
                          if (reason) void run(() => dismissThread(id, reason), 'Dismissed.')
                        }}
                        className="rounded border border-border px-2 py-1 text-[11px]">
                  Dismiss
                </button>
              </div>
            )}
          </div>

          {thread.dismissedReason && (
            <p className="rounded border border-border px-2 py-1 text-[11px]
                          text-muted-foreground">
              Dismissed {fmtDate(thread.dismissedAt)} — {thread.dismissedReason}
            </p>
          )}

          {liveSignals.length > 0 && (
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2">
              <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                Coordination on this subject
              </p>
              {liveSignals.slice(0, 3).map((c) => (
                <p key={c.id} className="text-[10px] text-muted-foreground">
                  “{c.phrase.slice(0, 120)}{c.phrase.length > 120 ? '…' : ''}” —{' '}
                  {c.distinctAuthors} authors across {c.threadCount} threads,{' '}
                  <b>{c.confidence}% confidence</b>. It is a pattern, not a person.
                </p>
              ))}
            </div>
          )}
        </div>
      </Panel>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {/* ── the conversation ── */}
        <Panel>
          <div className="space-y-2 p-3">
            <p className="text-[11px] font-medium">The conversation</p>
            <ul className="space-y-2">
              {posts.map((p) => (
                <li key={p.id}
                    className={`flex ${p.ours ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg border p-2 ${p.ours
                    ? 'border-primary/40 bg-primary/5'
                    : p.lurker ? 'border-sky-500/30 bg-sky-500/5' : 'border-border'}`}>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-[11px] font-medium">
                        {p.ours ? 'us' : p.authorHandle ? `@${p.authorHandle}` : 'unknown'}
                      </span>
                      {p.lurker && (
                        <span className="inline-flex items-center gap-0.5 text-[9px]
                                         text-sky-700 dark:text-sky-400">
                          <Eye className="size-2.5" /> engaged without commenting
                        </span>
                      )}
                      <span className="text-[9px] text-muted-foreground">
                        {p.postedAt ? fmtRelative(p.postedAt) : 'time unknown'}
                      </span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-[12px]">{p.body}</p>
                    <MediaStrip items={(media ?? []).filter((m) => m.postId === p.id)}
                                threadId={thread.id} canFetch={canTriage} onChanged={load} />
                    {(p.likes != null || p.replies != null) && (
                      <div className="mt-0.5 flex gap-2">
                        <Metric label="likes" value={p.likes} />
                        <Metric label="replies" value={p.replies} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {(media ?? []).some((m) => m.postId == null) && (
              <div className="rounded border border-dashed border-border p-2">
                <p className="text-[10px] text-muted-foreground">
                  Carried by this thread, not attached to a single post.
                </p>
                <MediaStrip items={(media ?? []).filter((m) => m.postId == null)}
                            threadId={thread.id} canFetch={canTriage} onChanged={load} />
              </div>
            )}

            {canTriage && thread.platformCode === 'MANUAL' && (
              <div className="space-y-1.5 rounded border border-dashed border-border p-2">
                <p className="text-[11px] font-medium">Add a reply you can see</p>
                <p className="text-[10px] text-muted-foreground">
                  A hand-entered thread is only as complete as what somebody types into it.
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide
                                     text-muted-foreground">Handle</span>
                    <input value={replyAuthor} onChange={(e) => setReplyAuthor(e.target.value)}
                           placeholder="@someone"
                           className="h-7 w-40 rounded border border-input bg-background px-2
                                      text-[12px]" />
                  </label>
                  <label className="flex flex-1 flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide
                                     text-muted-foreground">What they said</span>
                    <input value={reply} onChange={(e) => setReply(e.target.value)}
                           className="h-7 rounded border border-input bg-background px-2
                                      text-[12px]" />
                  </label>
                  <label className="flex items-center gap-1 pb-1 text-[10px]">
                    <input type="checkbox" checked={replyLurker}
                           onChange={(e) => setReplyLurker(e.target.checked)} />
                    liked but did not comment
                  </label>
                  <button type="button" disabled={busy || !reply.trim()}
                          onClick={() => void run(async () => {
                            // The @ is punctuation the UI adds back when it renders, so it is
                            // stripped once here. Storing it would print @@handle, and worse,
                            // would make "@jane" and "jane" two different people to the
                            // coordination scan.
                            const handle = replyAuthor.trim().replace(/^@/, '')
                            await addPost(id, {
                              authorHandle: handle || null,
                              body: reply.trim(),
                              lurker: replyLurker,
                            })
                            if (replyLurker && handle) {
                              await noteLurker(id, { handle, signalCode: 'LIKED' })
                            }
                            setReply('')
                            setReplyAuthor('')
                            setReplyLurker(false)
                          })}
                          className="rounded border border-border px-2 py-1 text-[11px]
                                     disabled:opacity-50">
                    Add
                  </button>
                </div>
              </div>
            )}

            {lurkers.length > 0 && (
              <p className="text-[10px] text-muted-foreground">
                {lurkers.length} identified as engaging without commenting. They are the people
                still deciding — a LURKER_OUTREACH draft goes under their own comment, not at
                the person shouting.
              </p>
            )}
          </div>
        </Panel>

        {/* ── reading it, and answering it ── */}
        <div className="space-y-3">
          <Panel>
            <div className="space-y-1.5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] font-medium">How it reads</p>
                {canTriage && (
                  <div className="flex gap-1.5">
                    <button type="button" disabled={busy}
                            onClick={() => void run(() => requestAssessment(id),
                              'Asked the council to read it. Reading posts nothing.')}
                            className="inline-flex items-center gap-1 rounded border
                                       border-border px-2 py-0.5 text-[11px]">
                      <Sparkles className="size-3 text-primary" /> Ask the council
                    </button>
                    <button type="button" onClick={() => setAssessing((v) => !v)}
                            className="rounded border border-border px-2 py-0.5 text-[11px]">
                      {assessing ? 'Close' : 'Score it myself'}
                    </button>
                  </div>
                )}
              </div>

              {latest ? (
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ThreatScore score={latest.threatScore} />
                    <SentimentChip code={latest.sentimentCode} />
                    <Pill className="border-border">{label(latest.framingCode)}</Pill>
                    <span className="text-[10px] text-muted-foreground">
                      {latest.advisorRunId ? 'by the council' : 'by a person'} ·{' '}
                      {fmtRelative(latest.assessedAt)}
                    </span>
                  </div>
                  <p className="text-[11px]">{latest.rationale}</p>
                  {assessments.length > 1 && (
                    <details>
                      <summary className="cursor-pointer text-[10px] text-muted-foreground">
                        {assessments.length - 1} earlier reading
                        {assessments.length === 2 ? '' : 's'} — nothing is overwritten
                      </summary>
                      <ul className="mt-1 space-y-1">
                        {assessments.slice(1).map((a) => (
                          <li key={a.id} className="text-[10px] text-muted-foreground">
                            {a.threatScore}/10 · {a.sentimentCode.toLowerCase()} ·{' '}
                            {fmtRelative(a.assessedAt)} — {a.rationale}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Nobody has read this thread yet. That is not the same as it being harmless.
                </p>
              )}

              {assessing && canTriage && (
                <div className="mt-1 space-y-1.5 rounded border border-dashed border-border p-2">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wide
                                     text-muted-foreground">Threat — {score}/10</span>
                    <input type="range" min={1} max={10} value={score}
                           onChange={(e) => setScore(Number(e.target.value))} />
                    <span className="text-[10px] text-muted-foreground">
                      Score the argument and its reach, never the rudeness.
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <select value={sentiment} onChange={(e) => setSentiment(e.target.value)}
                            className="h-7 rounded border border-input bg-background px-2
                                       text-[12px]">
                      {SENTIMENTS.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                    </select>
                    <select value={framing} onChange={(e) => setFraming(e.target.value)}
                            className="h-7 rounded border border-input bg-background px-2
                                       text-[12px]">
                      {FRAMINGS.map((f) => <option key={f} value={f}>{label(f)}</option>)}
                    </select>
                  </div>
                  <textarea value={rationale} onChange={(e) => setRationale(e.target.value)}
                            rows={2} placeholder="Why this score. At least 20 characters."
                            className="w-full rounded border border-input bg-background px-2
                                       py-1 text-[12px]" />
                  <button type="button" disabled={busy || rationale.trim().length < 20}
                          onClick={() => void run(async () => {
                            await recordAssessment(id, {
                              threatScore: score, sentimentCode: sentiment,
                              framingCode: framing, rationale: rationale.trim(),
                            })
                            setRationale('')
                            setAssessing(false)
                          }, 'Reading recorded.')}
                          className="rounded bg-primary px-3 py-1 text-[11px]
                                     text-primary-foreground disabled:opacity-50">
                    Record it
                  </button>
                </div>
              )}
            </div>
          </Panel>

          <CouncilStrip runs={councilRuns} />

          <Panel>
            <div className="space-y-2 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] font-medium">Replies we could send</p>
                {canDraft && (
                  <div className="flex gap-1.5">
                    <button type="button" disabled={busy}
                            onClick={() => void run(() => requestDrafts(id),
                              'Asked the council. Drafts cannot post themselves.')}
                            className="inline-flex items-center gap-1 rounded border
                                       border-border px-2 py-0.5 text-[11px]">
                      <Sparkles className="size-3 text-primary" /> Ask the council
                    </button>
                    <button type="button" onClick={() => setDrafting((v) => !v)}
                            className="rounded border border-border px-2 py-0.5 text-[11px]">
                      {drafting ? 'Close' : 'Write one'}
                    </button>
                  </div>
                )}
              </div>

              {drafting && canDraft && (
                <div className="space-y-1.5 rounded border border-dashed border-border p-2">
                  <div className="flex flex-wrap gap-2">
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">Tactic</span>
                      <select value={tactic} onChange={(e) => setTactic(e.target.value)}
                              className="h-7 rounded border border-input bg-background px-2
                                         text-[12px]">
                        {TACTICS.map(([code]) => (
                          <option key={code} value={code}>{label(code)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] uppercase tracking-wide
                                       text-muted-foreground">Voice</span>
                      <select value={personaId} onChange={(e) => setPersonaId(e.target.value)}
                              className="h-7 rounded border border-input bg-background px-2
                                         text-[12px]">
                        <option value="">No persona</option>
                        {personas.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {TACTICS.find(([c]) => c === tactic)?.[1]}
                    {personaId && ` · ${personas.find((p) => p.id === personaId)?.functionNote}`}
                  </p>
                  <textarea value={draftBody} onChange={(e) => setDraftBody(e.target.value)}
                            rows={3} placeholder="The exact text that would be posted."
                            className="w-full rounded border border-input bg-background px-2
                                       py-1 text-[12px]" />
                  <textarea value={draftRationale} rows={2}
                            onChange={(e) => setDraftRationale(e.target.value)}
                            placeholder="What this answers and why this tactic. 20 characters
                                         minimum — the approver is relying on it."
                            className="w-full rounded border border-input bg-background px-2
                                       py-1 text-[12px]" />
                  <button type="button"
                          disabled={busy || !draftBody.trim()
                                    || draftRationale.trim().length < 20}
                          onClick={() => void run(async () => {
                            await recordDraft(id, {
                              tacticCode: tactic,
                              personaId: personaId || null,
                              body: draftBody.trim(),
                              rationale: draftRationale.trim(),
                            })
                            setDraftBody(''); setDraftRationale(''); setDrafting(false)
                          }, 'Draft saved. It cannot post itself.')}
                          className="rounded bg-primary px-3 py-1 text-[11px]
                                     text-primary-foreground disabled:opacity-50">
                    Save the draft
                  </button>
                </div>
              )}

              {drafts.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Nothing drafted. A draft is a proposal — it reaches a platform only when a
                  person approves it, and their name goes on the record.
                </p>
              ) : (
                <ul className="space-y-2">
                  {drafts.map((d) => (
                    <li key={d.id} className="rounded border border-border p-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Pill className="border-border">{label(d.tacticCode)}</Pill>
                          {d.personaName && (
                            <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700
                                             dark:text-sky-400">{d.personaName}</Pill>
                          )}
                          <Pill className={d.statusCode === 'DEPLOYED'
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                            : d.statusCode === 'REJECTED' || d.statusCode === 'FAILED'
                            ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                            : 'border-border'}>{label(d.statusCode)}</Pill>
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {d.advisorRunId ? 'drafted by the council' : 'written by a person'}
                        </span>
                      </div>

                      {editing === d.id ? (
                        <div className="mt-1 space-y-1">
                          <textarea value={editBody} rows={3}
                                    onChange={(e) => setEditBody(e.target.value)}
                                    className="w-full rounded border border-input bg-background
                                               px-2 py-1 text-[12px]" />
                          <div className="flex gap-1.5">
                            <button type="button" disabled={busy}
                                    onClick={() => void run(async () => {
                                      await editDraft(d.id, editBody)
                                      setEditing(null)
                                    }, 'Edited. The original is kept on the record.')}
                                    className="rounded bg-primary px-2 py-0.5 text-[10px]
                                               text-primary-foreground">Save</button>
                            <button type="button" onClick={() => setEditing(null)}
                                    className="rounded border border-border px-2 py-0.5
                                               text-[10px]">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-1 whitespace-pre-wrap text-[12px]">{d.body}</p>
                      )}

                      {d.assetId && <DraftAsset assetId={d.assetId} />}

                      <p className="mt-1 text-[10px] text-muted-foreground">{d.rationale}</p>
                      {d.editedFrom && (
                        <details className="mt-0.5">
                          <summary className="cursor-pointer text-[10px] text-muted-foreground">
                            What the council originally wrote
                          </summary>
                          <p className="mt-0.5 whitespace-pre-wrap text-[10px]
                                        text-muted-foreground">{d.editedFrom}</p>
                        </details>
                      )}
                      {d.reviewNote && (
                        <p className="mt-0.5 text-[10px] text-rose-600">{d.reviewNote}</p>
                      )}

                      {d.statusCode === 'DRAFT' && (
                        <div className="mt-1.5 flex flex-wrap items-end gap-1.5">
                          {canDraft && (
                            <>
                              <button type="button"
                                      onClick={() => { setEditing(d.id); setEditBody(d.body) }}
                                      className="rounded border border-border px-2 py-0.5
                                                 text-[10px]">Edit</button>
                              <button type="button"
                                      onClick={() => setRejecting(
                                        rejecting === d.id ? null : d.id)}
                                      className="rounded border border-border px-2 py-0.5
                                                 text-[10px]">Reject</button>
                            </>
                          )}
                          {canDeploy && (
                            <>
                              <select value={accountId}
                                      onChange={(e) => setAccountId(e.target.value)}
                                      className="h-6 rounded border border-input bg-background
                                                 px-1 text-[10px]">
                                {postable.length === 0 && (
                                  <option value="">no account configured</option>
                                )}
                                {postable.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    @{a.handle} ({a.postedToday}/{a.dailyCap} today)
                                  </option>
                                ))}
                              </select>
                              <button type="button" disabled={busy || !accountId}
                                      onClick={() => void run(
                                        () => approveDraft(d.id, accountId),
                                        'Approved. Your name is on the ledger row; it sends '
                                        + 'after a short random delay.')}
                                      className="inline-flex items-center gap-1 rounded
                                                 bg-primary px-2 py-0.5 text-[10px]
                                                 text-primary-foreground disabled:opacity-50">
                                <Send className="size-2.5" /> Approve &amp; deploy
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {rejecting === d.id && (
                        <div className="mt-1 flex flex-wrap items-end gap-1.5">
                          <input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                                 placeholder="Why not? 10 characters minimum"
                                 className="h-6 flex-1 rounded border border-input bg-background
                                            px-2 text-[10px]" />
                          <button type="button"
                                  disabled={busy || rejectNote.trim().length < 10}
                                  onClick={() => void run(async () => {
                                    await rejectDraft(d.id, rejectNote.trim())
                                    setRejecting(null); setRejectNote('')
                                  }, 'Rejected.')}
                                  className="rounded border border-rose-500/40 px-2 py-0.5
                                             text-[10px] text-rose-700 disabled:opacity-50
                                             dark:text-rose-400">Reject it</button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Panel>

          {deployments.length > 0 && (
            <Panel>
              <div className="space-y-1 p-3">
                <p className="text-[11px] font-medium">What we sent, and who approved it</p>
                <ul className="space-y-1">
                  {deployments.map((d) => (
                    <li key={d.id} className="rounded border border-border p-1.5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[10px] text-muted-foreground">
                          @{d.accountHandle} · {label(d.statusCode)}
                          {d.jitterSeconds != null && ` · sent after ${d.jitterSeconds}s`}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtDate(d.approvedAt)}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap text-[11px]">{d.postedBody}</p>
                      {d.failureReason && (
                        <p className="text-[10px] text-rose-600">{d.failureReason}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  )
}


/**
 * What the council was asked, and what came back.
 *
 * <p>This panel is the fix for the bug that produced it. Asking the council started a run whose
 * id was thrown away, so the council answered into the advisory console and this screen never
 * changed — and an operator could not tell "still thinking" from "answered and had nothing to
 * add" from "broke". Three states that looked identical, one of which meant do nothing and one
 * of which meant go and look.
 *
 * <p>Settled runs that produced something are not shown. Their output IS the reading and the
 * drafts on this page, and a row saying "materialised 3 drafts" above three drafts is noise.
 */
function CouncilStrip({ runs }: { runs: CouncilRun[] }) {
  const worth = runs.filter((r) => r.status !== 'MATERIALISED')
  if (worth.length === 0) return null

  return (
    <Panel>
      <ul className="divide-y divide-border">
        {worth.map((run) => (
          <li key={run.runId} className="flex flex-wrap items-start gap-2 p-2.5">
            <Sparkles className={`mt-0.5 size-3.5 shrink-0 ${
              run.status === 'PENDING' ? 'animate-pulse text-primary'
              : run.status === 'FAILED' ? 'text-rose-600' : 'text-muted-foreground'}`} />
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-[11px] font-medium">
                {run.status === 'PENDING'
                  ? 'The council is reading this thread…'
                  : run.status === 'EMPTY'
                    ? 'The council answered and proposed nothing'
                    : 'The council could not answer'}
              </p>
              {run.note && (
                <p className="text-[10px] text-muted-foreground">{run.note}</p>
              )}
              <p className="text-[10px] text-muted-foreground">
                {run.playbookCode === 'THREAD_TRIAGE' ? 'Triage' : 'Counter-drafts'}
                {' · asked '}{fmtRelative(run.requestedAt)}
              </p>
            </div>
            {run.status === 'PENDING' && (
              <span className="text-[10px] text-muted-foreground">
                It appears here when it lands.
              </span>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/**
 * The images and video a post carried.
 *
 * ## Why the four states are rendered differently
 *
 * A CDN link rots exactly when it matters — the doctored tally sheet is deleted the moment it
 * starts costing somebody — so what we hold a copy of, and what we merely know the address of,
 * are different facts about the evidence:
 *
 * - **STORED** — we have it. Shown.
 * - **AVAILABLE** — we know where it is and have not spent the bandwidth. A button, because
 *   video is megabytes and mostly never watched.
 * - **SKIPPED** — past the size ceiling. Nothing is wrong and no retry will help, so it must
 *   not read as a fault to chase.
 * - **FAILED** — the evidence is gone. This one has to look different from AVAILABLE, or a
 *   room believes it still holds something it has already lost.
 */
function MediaStrip({ items, threadId, canFetch, onChanged }: {
  items: ThreadMedia[]; threadId: string; canFetch: boolean; onChanged: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (items.length === 0) return null

  async function fetchOne(media: ThreadMedia) {
    setBusy(media.id)
    setError(null)
    try {
      await downloadThreadMedia(threadId, media.id)
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mt-1.5 space-y-1">
      {error && <p className="text-[10px] text-rose-600">{error}</p>}
      <div className="flex flex-wrap gap-1.5">
        {items.map((m) => {
          if (m.status === 'STORED' && m.url && m.kind === 'IMAGE') {
            return (
              <a key={m.id} href={m.url} target="_blank" rel="noreferrer"
                 className="block overflow-hidden rounded border border-border">
                <img src={m.url} alt="Attached to this post" loading="lazy"
                     className="h-24 w-24 object-cover" />
              </a>
            )
          }
          if (m.status === 'STORED' && m.url && m.kind === 'VIDEO') {
            return (
              <video key={m.id} src={m.url} controls preload="metadata"
                     className="h-24 rounded border border-border" />
            )
          }
          if (m.status === 'AVAILABLE') {
            return (
              <button key={m.id} type="button" disabled={!canFetch || busy === m.id}
                      onClick={() => void fetchOne(m)}
                      className="inline-flex items-center gap-1 rounded border border-border
                                 px-2 py-1 text-[10px] hover:bg-muted/40 disabled:opacity-50">
                <Download className="size-3" />
                {busy === m.id
                  ? 'Fetching…'
                  : m.kind === 'VIDEO' ? 'Get the video' : 'Get the image'}
              </button>
            )
          }
          // SKIPPED and FAILED. Both name the reason, because "no video" explains nothing and
          // the two are not the same problem.
          return (
            <span key={m.id}
                  className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px]
                              ${m.status === 'FAILED'
                                ? 'border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400'
                                : 'border-border text-muted-foreground'}`}
                  title={m.failureReason ?? undefined}>
              <FileWarning className="size-3" />
              {m.kind === 'VIDEO' ? 'Video' : 'Image'}{' '}
              {m.status === 'FAILED' ? 'lost' : 'too large'}
              {m.sourceUrl && (
                <a href={m.sourceUrl} target="_blank" rel="noreferrer" className="underline">
                  try the source
                </a>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}
